
import { t } from '../i18n';

export function formatWorkoutDetails(day) {
  const { type, reps, rep_duration, pace, recovery, target, distance_km, progression_last_min, progression_pace, warmup_km, cooldown_km, work_km } = day;

  if (type === 'rest') return null;

  if (type === 'easy') {
    return `${distance_km} ${t('common.km')} · ${pace}/${t('common.km')}`;
  }

  if (type === 'long_run') {
    const { progression_distance_km, progression_pace, warmup_km, cooldown_km } = day;
    const displayPace = paceWithTolerance(pace);

    if (progression_distance_km && progression_pace) {
      const totalMain = distance_km - (warmup_km || 0) - (cooldown_km || 0);
      const easyKm = Math.round((totalMain - progression_distance_km) * 10) / 10;
      return `${distance_km} ${t('common.km')} · ${easyKm}km @ ${displayPace}/km + ${progression_distance_km}km @ ${progression_pace}/km`;
    }

    return `${distance_km} ${t('common.km')} · ${displayPace}/${t('common.km')}`;
  }

  if (type === 'short_reps' || type === 'hills') {
    const unit = day.unit || (target ? `${target.distance_m}m` : '');
    const timeRange = target ? formatTargetTime(target) : '';
    const targetPace = target ? formatTargetPace(target) : '';
    const totalKm = (warmup_km || 0) + (work_km || 0) + (cooldown_km || 0);
    const totalDisplay = totalKm > 0 ? ` · ${totalKm} ${t('common.km')}` : '';
    const paceDisplay = targetPace ? ` (${targetPace}/${t('common.km')})` : '';
    return `${reps}×${unit} ${t('workouts.for_time')} ${timeRange}${paceDisplay}${recovery ? `, ${t('workouts.recovery')} ${recovery}` : ''}${totalDisplay}`;
  }

  if (type === 'threshold' || type === 'medium_intervals' || type === 'short_intervals' || type === 'long_intervals') {
    return `${reps}×${rep_duration} @ ${paceWithTolerance(pace)}/${t('common.km')}${recovery ? `, ${t('workouts.recovery')} ${recovery}` : ''}`;
  }

  if (type === 'race') {
    const raceKm = day.work_km;
    const totalKm = distance_km;
    return `${raceKm} ${t('common.km')} @ ${pace}/${t('common.km')}${totalKm ? ` (${t('journal.total')}: ${totalKm} ${t('common.km')})` : ''}`;
  }

  if (type === 'race_pace_intervals') {
    if (target) {
      const timeRange = formatTargetTime(target);
      const targetPace = formatTargetPace(target);
      return `${reps}×${target.distance_m}m ${t('workouts.for_time')} ${timeRange} (${targetPace}/${t('common.km')})${recovery ? `, ${t('workouts.recovery')} ${recovery}` : ''}`;
    }
    return `${reps}×${rep_duration}${recovery ? `, ${t('workouts.recovery')} ${recovery}` : ''}`;
  }

  if (type === 'prerace_intervals') {
    return `${reps}×${day.unit} @ ${pace}/${t('common.km')}, ${recovery}`;
  }

  return `${distance_km ? distance_km + ' ' + t('common.km') : ''}${pace ? ` · ${pace}/${t('common.km')}` : ''}`;
}


export function formatWorkoutBreakdown(day) {
  const { type, warmup_km, work_km, recovery_km, cooldown_km, distance_km,
          pace, target, progression_distance_km, progression_last_min, progression_pace, notes } = day;

  const parts = [];

  if (warmup_km && warmup_km > 0) {
    parts.push({ label: t('workouts.warmup'), value: `${warmup_km} ${t('common.km')}` });
  }

  if (type === 'long_run') {
    const totalMain = distance_km - (warmup_km || 0) - (cooldown_km || 0);
    const mpKm = progression_distance_km || 0;
    const easyKm = Math.round((totalMain - mpKm) * 10) / 10;
    const displayPace = paceWithTolerance(pace);

    if (easyKm > 0) {
      parts.push({ label: t('workouts.easy_part'), value: `${easyKm.toFixed(1)} ${t('common.km')} @ ${displayPace}/${t('common.km')}` });
    }
    if (mpKm > 0) {
      parts.push({ label: t('workouts.mp_segment'), value: `${mpKm.toFixed(1)} ${t('common.km')} @ ${progression_pace}/${t('common.km')}` });
    } else if (work_km && work_km > 0) {
      if (pace) {
        const workDisplayPace = paceWithTolerance(pace);
        parts.push({ label: t('workouts.main_part'), value: `${work_km} ${t('common.km')} @ ${workDisplayPace}/${t('common.km')}` });
      } else if (target) {
        const targetPace = formatTargetPace(target);
        parts.push({ label: t('workouts.main_part'), value: `${work_km} ${t('common.km')} @ ${targetPace}/${t('common.km')}` });
      } else {
        parts.push({ label: t('workouts.main_part'), value: `${work_km} ${t('common.km')}` });
      }
      if (recovery_km && recovery_km > 0) {
        parts.push({ label: t('workouts.recovery_distance'), value: `${recovery_km} ${t('common.km')}` });
      }
    }
  } else if (type === 'easy') {
    parts.push({ label: t('workouts.distance'), value: `${distance_km} ${t('common.km')} @ ${pace}/${t('common.km')}` });
  }

  if (cooldown_km && cooldown_km > 0) {
    parts.push({ label: t('workouts.cooldown'), value: `${cooldown_km} ${t('common.km')}` });
  }

  const translatedNotes = Array.isArray(notes) ? notes.map(note => translateNote(note)) : [];

  return { parts, notes: translatedNotes };
}

export function paceToDecimal(paceStr) {
  const [min, sec] = paceStr.split(':').map(Number);
  return min + sec / 60;
}

export function paceWithTolerance(paceStr, tolerancePct = 1.5) {
  if (!paceStr || paceStr.includes('-')) return paceStr;

  const decimal = paceToDecimal(paceStr);
  const low = decimal * (1 - tolerancePct / 100);
  const high = decimal * (1 + tolerancePct / 100);

  return `${formatPaceFromMinPerKm(low)}-${formatPaceFromMinPerKm(high)}`;
}

function formatMinutes(totalMin) {
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}ч ${m}мин` : `${m}мин`;
}

function formatSecondsAsTime(sec) {
  if (sec < 60) return `${sec}${t('common.sec_short')}`;
  const min = Math.floor(sec / 60);
  const rem = Math.round(sec % 60);
  return `${min}:${String(rem).padStart(2, '0')}`;
}

function paceFromSeconds(distance_m, sec) {
  const paceMinPerKm = (sec / 60) / (distance_m / 1000);
  const min = Math.floor(paceMinPerKm);
  const secPart = Math.round((paceMinPerKm - min) * 60);
  return `${min}:${String(secPart).padStart(2, '0')}`;
}

function formatTargetTime(target) {
  if (!target) return '';
  return `${formatSecondsAsTime(target.time_low_sec)}-${formatSecondsAsTime(target.time_high_sec)}`;
}

function formatTargetPace(target) {
  if (!target) return '';
  const avgSec = (target.time_low_sec + target.time_high_sec) / 2;
  return paceFromSeconds(target.distance_m, avgSec);
}

// Функция перевода заметок
function translateNote(note) {
  if (!note) return note;

  const translations = {
    'target:': t('workouts.target') + ':',
    'warmup:': t('workouts.warmup') + ':',
    'cooldown:': t('workouts.cooldown') + ':',
    'recovery:': t('workouts.recovery') + ':',
    'pace:': t('workouts.pace') + ':',
    's': t('common.sec_short'),
    'km': t('common.km'),
    'm': 'm',
    '×': '×',
    'hills': t('workouts.hills'),
  };

  let translated = note;
  Object.entries(translations).forEach(([key, value]) => {
    translated = translated.replace(new RegExp(key, 'g'), value);
  });

  return translated;
}

export function formatPaceFromMinPerKm(paceMinPerKm) {
  if (paceMinPerKm == null) return '';
  const min = Math.floor(paceMinPerKm);
  const sec = Math.round((paceMinPerKm - min) * 60);
  return `${min}:${String(sec).padStart(2, '0')}`;
}

export function formatDecimalPace(decimalMinPerKm) {
  if (decimalMinPerKm == null) return '—';
  const min = Math.floor(decimalMinPerKm);
  const sec = Math.round((decimalMinPerKm - min) * 60);
  return `${min}:${String(sec).padStart(2, '0')}`;
}

export function formatActualResult(day) {
  // приоритет — синк из intervals.icu (точнее ручного ввода)
  if (day.actual_distance_km != null) {
    const parts = [`${day.actual_distance_km} ${t('common.km')}`];
    if (day.actual_avg_pace_min_per_km != null) {
      parts.push(`${formatPaceFromMinPerKm(day.actual_avg_pace_min_per_km)}/${t('common.km')}`);
    }
    if (day.actual_duration_sec != null) {
      const min = Math.floor(day.actual_duration_sec / 60);
      parts.push(min >= 60 ? formatMinutes(min) : `${min}:${String(day.actual_duration_sec % 60).padStart(2, '0')}`);
    }
    return parts.join(' · ');
  }

  // fallback — ручной ввод (если синка не было)
  if (day.avg_pace || day.duration_min) {
    const parts = [];
    if (day.duration_min) parts.push(formatMinutes(Math.round(day.duration_min)));
    if (day.avg_pace) parts.push(`${day.avg_pace}/${t('common.km')}`);
    if (day.rpe) parts.push(`RPE ${day.rpe}`);
    return parts.join(' · ');
  }

  return null;
}